import { observer } from "mobx-react-lite";
import { useRef, type CSSProperties, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { spaceHomeSubPageShellCssVars } from "../../space/home/Config";
import { PAGE_SHELL_BG } from "../../utils/pageShellBg";
import { useDocumentSpaceTheme } from "../../utils/useDocumentSpaceTheme";
import { h5NavigateTo } from "../../utils/miniProgramEnv";
import { useLfPageTitle } from "../../utils/useLfPageTitle";
import { usePageShellBg } from "../../utils/usePageShellBg";
import { useSafeAreaTopPad } from "../../utils/useSafeAreaTopPad";
import { H5BackButton } from "./H5BackButton";
import { h5BackButtonCssVars } from "./H5BackButtonConfig";
import "./H5SubPageShell.css";

type Props = {
  /** H5 内顶栏展示的功能标题，如「个人资料」 */
  title: string;
  /** 可选：标题下方副文案（如股票代码），与 title 组成双行标题 */
  titleSubtitle?: string;
  /** 可选：根节点额外 class，便于单页覆盖顶栏样式 */
  shellClassName?: string;
  /** 可选：已知空间名时优先用于 document.title */
  spaceName?: string | null;
  backTo?: string;
  onBack?: () => void;
  headerRight?: ReactNode;
  children: ReactNode;
};

/** 空间内二级页通用壳：顶栏返回 + 标题 + 可滚动内容区 */
export const H5SubPageShell = observer(function H5SubPageShell(props: Props) {
  const {
    title,
    titleSubtitle,
    shellClassName,
    spaceName,
    backTo,
    onBack: onBackProp,
    headerRight,
    children,
  } = props;
  const navigate = useNavigate();
  const headerRef = useRef<HTMLElement>(null);
  const safeTopPad = useSafeAreaTopPad(headerRef);
  const pageTitle = useLfPageTitle(spaceName);

  useDocumentSpaceTheme(true);

  usePageShellBg({
    bg: PAGE_SHELL_BG.spaceLight,
    front: "#000000",
    title: pageTitle,
  });

  const onBack = () => {
    if (onBackProp) {
      onBackProp();
      return;
    }
    if (backTo) {
      h5NavigateTo(navigate, backTo, { replace: true });
      return;
    }
    navigate(-1);
  };

  const headerStyle: CSSProperties | undefined =
    safeTopPad > 0
      ? ({ "--h5-sub-page-header-safe-top-pad": `${safeTopPad}px` } as CSSProperties)
      : undefined;

  return (
    <div
      className={["h5-sub-page", shellClassName].filter(Boolean).join(" ")}
      style={{ ...h5BackButtonCssVars(), ...spaceHomeSubPageShellCssVars() }}
    >
      <header
        ref={headerRef}
        className={[
          "h5-sub-page__header",
          titleSubtitle ? "h5-sub-page__header--two-line-title" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        style={headerStyle}
      >
        <div className="h5-sub-page__header-side h5-sub-page__header-side--left">
          <H5BackButton
            className="h5-sub-page__back"
            iconClassName="h5-back-button__icon h5-back-button__icon--invert"
            onClick={onBack}
          />
        </div>
        {titleSubtitle ? (
          <div className="h5-sub-page__title-wrap">
            <span className="h5-sub-page__title-name">{title}</span>
            <span className="h5-sub-page__title-sub">{titleSubtitle}</span>
          </div>
        ) : (
          <h1 className="h5-sub-page__title">{title}</h1>
        )}
        <div className="h5-sub-page__header-side h5-sub-page__header-side--right">
          {headerRight ?? <span className="h5-sub-page__header-spacer" aria-hidden />}
        </div>
      </header>
      <div className="h5-sub-page__body">{children}</div>
    </div>
  );
});
